import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import type { CamposDeLaFila } from "@/lib/informes";
import { numeroEscrito } from "@/lib/numeroEscrito";

import {
  GrupoDeIdentificacion,
  GrupoDeValorizacion,
  RotuloDeGrupo,
  ValorEstimado,
} from "./CamposDeConstruccion";
import { FotosDeLaFila } from "./FotosDeLaFila";
import {
  camposDesdeConstruccion,
  CONSTRUCCION_VACIA,
  type FormularioDeConstruccion,
} from "./construcciones";

interface FormularioDeNuevaConstruccionProps {
  ocupada: boolean;
  /** Crea la fila y, con el id que devuelve, sube las fotos retenidas. */
  onAgregar: (campos: CamposDeLaFila, fotos: File[]) => void;
  onCerrar: () => void;
}

/**
 * Lo que falta para poder agregar, dicho en palabras. El diseño pide TIPO Y
 * SUPERFICIE: sin superficie no hay total, y una fila sin total es justo lo que
 * la lista marca como pendiente.
 */
function loQueFalta(formulario: FormularioDeConstruccion): string[] {
  const superficie = numeroEscrito(formulario.superficie_m2);

  return [
    formulario.tipo_edificacion.trim() === "" && "tipo de edificación",
    (superficie === null || superficie <= 0) && "superficie",
  ].filter((falta): falta is string => falta !== false);
}

/**
 * El formulario EN LÍNEA de la lista: agrega una construcción nueva.
 *
 * SIEMPRE AGREGA, nunca corrige: corregir una fila abre el modal, con su propio
 * estado, así que un borrador a medio escribir acá no se pierde al abrir otra
 * fila. Se desmonta al cerrar, y con él se va el borrador y las fotos
 * retenidas — "Cancelar" promete eso.
 *
 * LAS FOTOS SE RETIENEN en el navegador y viajan con el id que devuelve el
 * alta (`onAgregar`), igual que la imagen satelital en el alta del informe:
 * hasta ahí no hay fila a la cual colgarlas.
 */
export function FormularioDeNuevaConstruccion({
  ocupada,
  onAgregar,
  onCerrar,
}: FormularioDeNuevaConstruccionProps) {
  const [formulario, setFormulario] = useState<FormularioDeConstruccion>(CONSTRUCCION_VACIA);
  const [fotos, setFotos] = useState<File[]>([]);
  const [rechazoDeFoto, setRechazoDeFoto] = useState<string | null>(null);

  const faltan = loQueFalta(formulario);
  const puedeAgregar = faltan.length === 0;

  function escribir(cambio: Partial<FormularioDeConstruccion>) {
    setFormulario((actual) => ({ ...actual, ...cambio }));
  }

  function agregar() {
    if (!puedeAgregar) return;

    onAgregar(camposDesdeConstruccion(formulario), fotos);
    onCerrar();
  }

  const comunes = { idPrefijo: "construccion", formulario, ocupada, escribir };

  return (
    <div
      id="nueva-construccion"
      className="bg-superficie-sutil border-divisor flex scroll-mt-5 flex-col gap-3.5 border-y-[1.5px] px-5 pt-4 pb-4.5"
    >
      <div className="flex flex-wrap items-center gap-2.5">
        <h4 className="text-ink text-[0.84375rem] font-bold">Nueva construcción</h4>
        <span className="text-ink-muted text-caption">
          Completa al menos tipo y superficie; la valorización puede quedar para después.
        </span>
      </div>

      <RotuloDeGrupo>Identificación</RotuloDeGrupo>
      <GrupoDeIdentificacion {...comunes} requeridos={["tipo_edificacion", "superficie_m2"]} />

      <RotuloDeGrupo aparte="Opcional al crear">Valorización</RotuloDeGrupo>
      <GrupoDeValorizacion {...comunes} />

      <ValorEstimado formulario={formulario} />

      <FotosDeLaFila
        titulo="Fotos de la construcción (una o más)"
        etiquetaDelSelector="Adjuntar fotos de la construcción"
        ocupada={ocupada}
        editando={false}
        pendientes={fotos}
        // Nunca corrige una fila que ya existe: `editando={false}` hace que se
        // dibuje la lista de PENDIENTES y no la de guardadas.
        guardadas={[]}
        urlDe={() => ""}
        alt="Foto de la construcción"
        error={rechazoDeFoto}
        onElegidas={(elegidos) => setFotos((actuales) => [...actuales, ...elegidos])}
        onRechazo={setRechazoDeFoto}
        onQuitarPendiente={(indice) => setFotos((actuales) => actuales.filter((_, i) => i !== indice))}
        onQuitarGuardada={() => {}}
      />

      <div className="flex flex-wrap items-center gap-3 pt-1">
        <Button type="button" onClick={agregar} disabled={ocupada || !puedeAgregar}>
          {ocupada ? <Spinner /> : null}
          Agregar a la lista
        </Button>
        <Button type="button" variant="ghost" onClick={onCerrar}>
          Cancelar
        </Button>
        {puedeAgregar ? null : (
          <span className="text-ink-hint text-caption">Falta: {faltan.join(" y ")}</span>
        )}
      </div>
    </div>
  );
}
